import { z } from "zod";

import { EconomicModel, PaymentDirection } from "../../types/paymentFees.types";

export interface RouteFormMessages {
  corridorRequired: string;
  providerRequired: string;
}

/**
 * Schema of the create-route form.
 *
 * The three identity fields are plain strings rather than enums because they carry composite or catalog
 * values (`"MEX|SPEI|MXN"`, an adapter code) that the dialog resolves against its catalogs — an enum here
 * would only restate a list that already lives in `paymentRoutes.constants.ts`. What the schema does enforce
 * is that a route can never be submitted half-identified: the backend would happily accept a corridor whose
 * provider cannot serve it, and the result is payouts stranded with their funds reserved.
 *
 * `is_active` is deliberately absent — see `CreatePaymentRoutePayload`.
 */
export function buildRouteFormSchema(messages: RouteFormMessages) {
  return z
    .object({
      /** `"<country>|<rail>|<currency>"` — one pick that fills the three corridor columns. */
      corridorKey: z.string(),
      direction: z.string(),
      /** Adapter code. Never rendered; see rule 12. */
      providerCode: z.string(),
      economicModel: z.enum([EconomicModel.PASS_THROUGH, EconomicModel.ABSORBED]),
    })
    .superRefine((values, ctx) => {
      if (!values.corridorKey) {
        ctx.addIssue({ code: "custom", path: ["corridorKey"], message: messages.corridorRequired });
      }
      if (values.direction !== PaymentDirection.PAYIN && values.direction !== PaymentDirection.PAYOUT) {
        ctx.addIssue({ code: "custom", path: ["direction"], message: messages.corridorRequired });
      }
      if (!values.providerCode) {
        ctx.addIssue({ code: "custom", path: ["providerCode"], message: messages.providerRequired });
      }
    });
}

export type RouteFormValues = z.input<ReturnType<typeof buildRouteFormSchema>>;

export const ROUTE_FORM_DEFAULTS: RouteFormValues = {
  corridorKey: "",
  direction: "",
  providerCode: "",
  economicModel: EconomicModel.PASS_THROUGH,
};
